import { JobOffer, Skill } from '../models/job';

const hasUncertainSkills = (skills: (string | Skill)[] | undefined): boolean => {
  if (!skills || skills.length === 0) {
    return false;
  }
  return skills.some(skill => typeof skill !== 'string' && skill.importance === 'uncertain');
};

/**
 * Check if a value is missing or empty
 */
const isMissingValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && !value.trim()) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'number' && value === 0) return false; // 0 is a valid number
  return false;
};

export interface FieldDefinition {
  name: string;
  value: unknown;
}

export interface MissingFieldsResult {
  hasBlockingFields: boolean;
  hasMissingFields: boolean;
  missingFields: string[];
  recommendedMissingFields: string[];
  criticalMissingFields: string[];
}

export const BLOCKING_FIELDS = [
  'Remote Policy',
  'Contract Type',
  'Location',
  'Years of Experience',
  'Required Degree',
  'Mission Role in Job',
  'Skills',
  'Company Name',
] as const;

export const RECOMMENDED_FIELDS = [
  'Company Sector',
  'Company Description',
] as const;

const FIELD_MAP: Record<string, keyof JobOffer> = {
  'Remote Policy': 'remotePolicy',
  'Contract Type': 'contractType',
  'Location': 'location',
  'Years of Experience': 'yearsOfExperience',
  'Required Degree': 'requiredDegree',
  'Mission Role in Job': 'mission_role_in_job',
  'Skills': 'skills',
  'Company Name': 'companyName',
  'Company Sector': 'companySector',
  'Company Description': 'companyDescription',
} as const;

export const getFieldValue = (job: JobOffer, fieldName: string): unknown => {
  const fieldKey = FIELD_MAP[fieldName];
  return fieldKey ? job[fieldKey] : undefined;
};

export const getBlockingFields = (job: JobOffer): FieldDefinition[] => {
  return BLOCKING_FIELDS.map((name) => ({
    name,
    value: getFieldValue(job, name),
  }));
};

export const getRecommendedFields = (job: JobOffer): FieldDefinition[] => {
  return RECOMMENDED_FIELDS.map((name) => ({
    name,
    value: getFieldValue(job, name),
  }));
};

export const detectMissingFields = (job: JobOffer | null | undefined): MissingFieldsResult => {
  if (!job) {
    return {
      hasBlockingFields: false,
      hasMissingFields: false,
      missingFields: [],
      recommendedMissingFields: [],
      criticalMissingFields: [],
    };
  }

  const blockingFields = getBlockingFields(job);
  const recommendedFields = getRecommendedFields(job);

  const missingBlocking = blockingFields.filter((field) => {
    if (field.name === 'Skills') {
      const skills = job.skills && job.skills.length > 0
        ? job.skills
        : [...(job.codingLanguages || []), ...(job.frameworksLibraries || [])];
      return isMissingValue(skills) || hasUncertainSkills(skills);
    }
    return isMissingValue(field.value);
  });

  const missingRecommended = recommendedFields.filter((field) => isMissingValue(field.value));
  
  const allMissing = [...missingBlocking, ...missingRecommended];
  const missingFieldNames = allMissing.map((field) => field.name);
  const recommendedMissingFieldNames = missingRecommended.map((field) => field.name);
  const criticalMissingFieldNames = missingBlocking.map((field) => field.name);

  return {
    hasBlockingFields: missingBlocking.length > 0,
    hasMissingFields: allMissing.length > 0,
    missingFields: missingFieldNames,
    recommendedMissingFields: recommendedMissingFieldNames,
    criticalMissingFields: criticalMissingFieldNames,
  };
};

/**
 * Check if a job is in DRAFT status with missing job description or company info
 * This is used to detect when a job returned to DRAFT after analysis failed
 */
export const hasMissingJobDescriptionOrCompanyInfo = (job: JobOffer | null | undefined): boolean => {
  if (!job) return false;
  
  // Check if job description is empty or just whitespace
  const hasDescription = job.description && job.description.trim().length > 0;
  
  // Check if company name exists (as a proxy for company info)
  const hasCompanyInfo = job.companyName && job.companyName.trim().length > 0;
  
  // Job is missing info if it has neither description nor company info
  return !hasDescription && !hasCompanyInfo;
};

